import React, { useMemo, useState } from "react";
import whatIsCoachConnectImg from "@/assets/catalog/coach-connect/what-is-coach-connect.webp";
import CpLandingShell from "../../components/CpLandingShell/CpLandingShell";
import CpButton from "../../components/CpButton/CpButton";
import CpCard from "../../components/CpCard/CpCard";
import CpSectionHeading from "../../components/CpSectionHeading/CpSectionHeading";
import CpTag from "../../components/CpTag/CpTag";
import CpAccordion, {
  CpAccordionItem,
} from "../../components/CpAccordion/CpAccordion";
import CpIcon from "../../components/CpIcon/CpIcon";
import {
  ALL_MONTHS,
  monthFilters,
  upcomingSessions,
} from "./coachConnectSessions";

const EMPTY_SCHEDULE = "New calls are added often — check back soon.";

const AUDIENCES = ["Owners", "Managers", "All Trades"];
const TIERS = ["Build", "Grow", "Lead", "Scale"];

const HERO_FEATURES = [
  { icon: "video", label: "Live group calls" },
  { icon: "calendar-days", label: "New topics every month" },
  { icon: "message-square", label: "Open Q&A" },
];

const BENEFITS = [
  {
    icon: "users",
    title: "Learn With Fellow Members",
    body: "Hear the questions other owners are asking — and the answers — on the same call.",
  },
  {
    icon: "headphones",
    title: "Straight From Your Coaches",
    body: "Every call is led by CertainPath coaches who work these problems with members every week.",
  },
  {
    icon: "list-checks",
    title: "Join Any and All Calls",
    body: "Budgets, financing, recruiting, culture, marketing. Pick the topics that matter to you this month.",
  },
  {
    icon: "message-square",
    title: "Bring Your Questions",
    body: "Each call leaves room for Q&A, so you leave with answers for your business.",
  },
];

const STEPS = [
  {
    title: "Pick a Topic",
    body: "Browse the schedule and choose the calls that fit what your business needs now.",
  },
  {
    title: "Register",
    body: "Save your spot for each call. Register for as many as you like.",
  },
  {
    title: "Join the Group Call",
    body: "Hear your coaches cover the topic with the other members on the call.",
  },
  {
    title: "Ask Your Questions",
    body: "Get answers for your business, then put them to work with your team.",
  },
];

const FAQS: CpAccordionItem[] = [
  {
    title: "Who can join Coach Connect calls?",
    body: "Coach Connect calls are open to CertainPath members. You are free to join any and all of them.",
  },
  {
    title: "What happens on a call?",
    body: "Your CertainPath coaches share information on that call’s topic, then answer questions from the members who join.",
  },
  {
    title: "Do I have to attend every call?",
    body: "No. Pick the topics that matter to your business and register for those.",
  },
  {
    title: "When are calls held?",
    body: "Current calls run at 10 AM CST. The schedule changes often, so check back for the latest dates and times.",
  },
];

const pillStyle: React.CSSProperties = {
  padding: "6px 14px",
  borderRadius: 999,
  background: "var(--white)",
  border: "1px solid var(--border-default)",
  fontSize: 13,
  fontWeight: 600,
};

const tierStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "6px 14px 6px 11px",
  borderRadius: 999,
  background: "var(--ink-500)",
  color: "var(--cream-100)",
  fontSize: 13,
  fontWeight: 700,
};

const rowLabelStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: "var(--grey-500)",
  marginRight: 4,
};

const eyebrowGoldStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 800,
  letterSpacing: ".14em",
  textTransform: "uppercase",
  color: "var(--gold-400)",
};

const stepCardStyle: React.CSSProperties = {
  background: "var(--white)",
  borderRadius: 16,
  padding: "28px 24px",
  width: "100%",
  boxSizing: "border-box",
  flex: 1,
};

const CoachConnectPage: React.FC = () => {
  const sessions = useMemo(() => upcomingSessions(new Date()), []);
  const filters = useMemo(() => monthFilters(sessions), [sessions]);
  const [month, setMonth] = useState(ALL_MONTHS);

  const next = sessions[0];
  const upNext = sessions.slice(1, 4);
  const shown =
    month === ALL_MONTHS ? sessions : sessions.filter((s) => s.month === month);

  return (
    <CpLandingShell title="Coach Connect">
      <section
        style={{
          background: "var(--cream-100)",
          padding: "clamp(56px, 7vw, 96px) 0",
        }}
      >
        <div
          style={{
            maxWidth: "var(--container-max)",
            margin: "0 auto",
            padding: "0 24px",
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit, minmax(min(100%, 440px), 1fr))",
            gap: 56,
            alignItems: "center",
          }}
        >
          <div>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 14px",
                borderRadius: 999,
                background: "var(--red-50)",
                border: "1px solid rgba(178,0,48,.15)",
                color: "var(--red-500)",
                fontSize: 12,
                fontWeight: 800,
                letterSpacing: ".14em",
                textTransform: "uppercase",
                lineHeight: 1.2,
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: "var(--red-500)",
                  boxShadow: "0 0 0 3px rgba(178,0,48,.18)",
                }}
              />
              Coaching
            </span>
            <h1
              style={{
                margin: "22px 0 0",
                fontSize: "clamp(48px, 7vw, 88px)",
                lineHeight: 1,
                letterSpacing: "-.035em",
                color: "var(--ink-500)",
              }}
            >
              <span style={{ fontWeight: 300 }}>Coach</span>{" "}
              <span style={{ fontWeight: 800, color: "var(--red-500)" }}>
                Connect
              </span>
            </h1>
            <p
              style={{
                margin: "22px 0 0",
                maxWidth: "30ch",
                fontSize: "clamp(20px, 1.9vw, 24px)",
                lineHeight: 1.4,
                letterSpacing: "-.01em",
                color: "var(--ink-500)",
              }}
            >
              Live group calls where your CertainPath coaches teach a topic and
              answer your questions.
            </p>

            <div
              style={{
                marginTop: 26,
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <span style={rowLabelStyle}>Built for</span>
              {AUDIENCES.map((a) => (
                <span key={a} style={pillStyle}>
                  {a}
                </span>
              ))}
            </div>

            <div
              style={{
                marginTop: 12,
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <span style={rowLabelStyle}>Available on</span>
              {TIERS.map((t) => (
                <span key={t} style={tierStyle}>
                  <CpIcon color="var(--gold-400)" name="check" size={13} />
                  {t}
                </span>
              ))}
            </div>

            <div
              style={{
                marginTop: 34,
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <CpButton href="#schedule" size="lg">
                See the Call Schedule
              </CpButton>
            </div>

            <div
              style={{
                marginTop: 40,
                paddingTop: 24,
                borderTop: "1px solid var(--border-default)",
                display: "flex",
                gap: 32,
                flexWrap: "wrap",
              }}
            >
              {HERO_FEATURES.map((f) => (
                <div
                  key={f.label}
                  style={{ display: "flex", alignItems: "center", gap: 12 }}
                >
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 12,
                      background: "var(--ink-500)",
                      display: "grid",
                      placeItems: "center",
                    }}
                  >
                    <CpIcon color="var(--gold-400)" name={f.icon} size={20} />
                  </div>
                  <span style={{ fontSize: 15, fontWeight: 700 }}>
                    {f.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div
            aria-label="Next Coach Connect call"
            style={{
              background: "var(--ink-500)",
              borderRadius: 24,
              boxShadow: "var(--shadow-lg)",
              overflow: "hidden",
              color: "var(--cream-100)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "16px 24px",
                borderBottom: "1px solid var(--border-inverse)",
              }}
            >
              <span style={{ fontSize: 14, fontWeight: 700 }}>
                Next Coach Connect call
              </span>
              <span style={eyebrowGoldStyle}>Live</span>
            </div>
            {next ? (
              <div
                style={{
                  padding: "28px 24px",
                  display: "grid",
                  gridTemplateColumns: "auto 1fr",
                  gap: 22,
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    width: 96,
                    height: 104,
                    borderRadius: 16,
                    background: "var(--cream-100)",
                    color: "var(--ink-500)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 800,
                      letterSpacing: ".14em",
                      textTransform: "uppercase",
                      color: "var(--red-500)",
                    }}
                  >
                    {next.mon}
                  </span>
                  <span
                    style={{
                      fontSize: 44,
                      fontWeight: 800,
                      letterSpacing: "-.03em",
                      lineHeight: 1,
                    }}
                  >
                    {next.day}
                  </span>
                </div>
                <div>
                  <h3
                    style={{
                      margin: 0,
                      fontSize: 24,
                      lineHeight: 1.2,
                      letterSpacing: "-.02em",
                      color: "var(--cream-100)",
                    }}
                  >
                    {next.title}
                  </h3>
                  <p
                    style={{
                      margin: "8px 0 0",
                      fontSize: 15,
                      color: "var(--ink-100)",
                    }}
                  >
                    {next.time} · Group call with your coaches
                  </p>
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  <CpButton href={next.url} variant="gold">
                    Register
                  </CpButton>
                </div>
              </div>
            ) : (
              <p
                style={{
                  margin: 0,
                  padding: "28px 24px",
                  fontSize: 15,
                  color: "var(--ink-100)",
                }}
              >
                {EMPTY_SCHEDULE}
              </p>
            )}
            {upNext.length > 0 && (
              <div
                style={{
                  padding: "20px 24px 24px",
                  background: "var(--ink-900)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                }}
              >
                <span style={eyebrowGoldStyle}>Up next</span>
                {upNext.map((s) => (
                  <div
                    key={s.url}
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      gap: 14,
                      padding: "10px 0",
                      borderTop: "1px solid var(--border-inverse)",
                    }}
                  >
                    <span
                      style={{
                        flex: "0 0 56px",
                        fontSize: 13,
                        fontWeight: 800,
                        color: "var(--gold-400)",
                      }}
                    >
                      {s.short}
                    </span>
                    <span
                      style={{
                        fontSize: 15,
                        fontWeight: 600,
                        color: "var(--cream-100)",
                      }}
                    >
                      {s.title}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <section
        style={{
          background: "var(--ink-500)",
          padding: "clamp(56px, 7vw, 96px) 0",
        }}
      >
        <div
          style={{
            maxWidth: 1160,
            margin: "0 auto",
            padding: "0 24px",
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit, minmax(min(100%, 340px), 1fr))",
            gap: "24px 64px",
            alignItems: "start",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
            <h2
              style={{
                margin: 0,
                color: "var(--gold-400)",
                fontSize: "clamp(30px, 3.2vw, 42px)",
                fontWeight: 800,
                letterSpacing: "-.025em",
                lineHeight: 1.12,
              }}
            >
              What is Coach Connect?
            </h2>
            <img
              alt="Coach Connect"
              src={whatIsCoachConnectImg}
              style={{
                display: "block",
                width: "100%",
                maxWidth: 420,
                height: "auto",
                borderRadius: 16,
              }}
            />
          </div>
          <div>
            <p
              style={{
                margin: 0,
                color: "var(--cream-100)",
                fontSize: "clamp(20px, 1.9vw, 24px)",
                fontWeight: 300,
                lineHeight: 1.45,
              }}
            >
              Our coaching team is here to help —{" "}
              <strong style={{ fontWeight: 800 }}>
                and sometimes it&apos;s better to learn alongside your fellow
                members.
              </strong>
            </p>
            <p
              style={{
                margin: "18px 0 0",
                color: "var(--cream-100)",
                fontSize: 17,
                lineHeight: 1.65,
              }}
            >
              Coach Connect calls are group calls where your CertainPath coaches
              share what&apos;s working on a specific topic and answer questions
              from the members who join. You&apos;re free to join any and all of
              them.
            </p>
          </div>
        </div>
      </section>

      <section
        style={{ background: "var(--sand-200)", padding: "var(--section-y) 0" }}
      >
        <div
          style={{
            maxWidth: "var(--container-max)",
            margin: "0 auto",
            padding: "0 24px",
          }}
        >
          <CpSectionHeading
            align="center"
            eyebrow="Why Members Join"
            title="Get Answers Without Waiting for Your Next Session"
          />
          <div
            data-stagger=""
            style={{
              display: "grid",
              gridTemplateColumns:
                "repeat(auto-fit, minmax(min(100%, 250px), 1fr))",
              gap: 24,
              marginTop: 48,
            }}
          >
            {BENEFITS.map((b) => (
              <CpCard key={b.title} padding="32px" tone="light">
                <CpIcon color="var(--red-500)" name={b.icon} size={26} />
                <h3
                  style={{ margin: "16px 0 8px", fontSize: "var(--text-h3)" }}
                >
                  {b.title}
                </h3>
                <p
                  style={{
                    margin: 0,
                    color: "var(--text-muted)",
                    fontSize: 15,
                    lineHeight: 1.6,
                  }}
                >
                  {b.body}
                </p>
              </CpCard>
            ))}
          </div>
        </div>
      </section>

      <section
        id="schedule"
        style={{ background: "var(--white)", padding: "var(--section-y) 0" }}
      >
        <div style={{ maxWidth: 1080, margin: "0 auto", padding: "0 24px" }}>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "flex-end",
              justifyContent: "space-between",
              gap: 24,
            }}
          >
            <CpSectionHeading
              eyebrow="Call Schedule"
              lead="All calls are at 10 AM CST. New dates are added often, so check back for the latest schedule."
              title="Upcoming Coach Connect Calls"
            />
            <div
              aria-label="Filter by month"
              role="tablist"
              style={{ display: "flex", gap: 8, flexWrap: "wrap" }}
            >
              {filters.map((label) => (
                <CpTag
                  key={label}
                  onClick={() => setMonth(label)}
                  selected={month === label}
                >
                  {label}
                </CpTag>
              ))}
            </div>
          </div>

          {shown.length > 0 ? (
            <ol
              data-stagger=""
              style={{
                listStyle: "none",
                margin: "40px 0 0",
                padding: 0,
                display: "flex",
                flexDirection: "column",
                gap: 14,
              }}
            >
              {shown.map((s) => (
                <li
                  key={s.url}
                  style={{
                    background: "var(--cream-100)",
                    border: "1px solid var(--border-default)",
                    borderRadius: 16,
                    padding: "22px 24px",
                    display: "grid",
                    gridTemplateColumns: "88px minmax(0, 1fr) auto",
                    gap: 24,
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      width: 88,
                      height: 88,
                      borderRadius: 14,
                      background: "var(--ink-500)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <span style={eyebrowGoldStyle}>{s.mon}</span>
                    <span
                      style={{
                        fontSize: 36,
                        fontWeight: 800,
                        letterSpacing: "-.03em",
                        lineHeight: 1,
                        color: "var(--cream-100)",
                      }}
                    >
                      {s.day}
                    </span>
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <h3
                      style={{
                        margin: 0,
                        fontSize: 20,
                        lineHeight: 1.3,
                        letterSpacing: "-.015em",
                        textWrap: "pretty",
                      }}
                    >
                      {s.title}
                    </h3>
                    {s.desc && (
                      <p
                        style={{
                          margin: "6px 0 0",
                          fontSize: 15,
                          lineHeight: 1.6,
                          color: "var(--text-muted)",
                          maxWidth: "64ch",
                          textWrap: "pretty",
                        }}
                      >
                        {s.desc}
                      </p>
                    )}
                    <p
                      style={{
                        margin: "8px 0 0",
                        fontSize: 13,
                        fontWeight: 700,
                        color: "var(--red-500)",
                      }}
                    >
                      {s.when}
                    </p>
                  </div>
                  <CpButton href={s.url}>Register</CpButton>
                </li>
              ))}
            </ol>
          ) : (
            <p
              style={{
                margin: "40px 0 0",
                fontSize: 15,
                color: "var(--text-muted)",
              }}
            >
              {EMPTY_SCHEDULE}
            </p>
          )}
        </div>
      </section>

      <section
        id="how-it-works"
        style={{ background: "var(--ink-500)", padding: "var(--section-y) 0" }}
      >
        <div
          style={{
            maxWidth: "var(--container-max)",
            margin: "0 auto",
            padding: "0 24px",
          }}
        >
          <CpSectionHeading
            align="center"
            eyebrow="How It Works"
            title="From Topic to Answers in Four Steps"
            tone="dark"
          />
          <ol
            data-stagger=""
            style={{
              listStyle: "none",
              margin: "48px 0 0",
              padding: 0,
              display: "grid",
              gridTemplateColumns:
                "repeat(auto-fit, minmax(min(100%, 240px), 1fr))",
              gap: 22,
            }}
          >
            {STEPS.map((step, i) => {
              const last = i === STEPS.length - 1;
              return (
                <li
                  key={step.title}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 18,
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: "50%",
                      ...(last
                        ? {
                            background: "var(--gold-400)",
                            color: "var(--ink-500)",
                          }
                        : {
                            border: "2px solid var(--gold-400)",
                            color: "var(--gold-400)",
                          }),
                      display: "grid",
                      placeItems: "center",
                      fontSize: 22,
                      fontWeight: 800,
                    }}
                  >
                    {i + 1}
                  </div>
                  <div style={stepCardStyle}>
                    <h3 style={{ margin: 0, fontSize: 18 }}>{step.title}</h3>
                    <p
                      style={{
                        margin: "10px 0 0",
                        fontSize: 15,
                        lineHeight: 1.55,
                        color: "var(--text-muted)",
                      }}
                    >
                      {step.body}
                    </p>
                  </div>
                </li>
              );
            })}
          </ol>
          <div
            style={{
              marginTop: 48,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <CpButton href="#schedule" size="lg">
              See the Call Schedule
            </CpButton>
          </div>
        </div>
      </section>

      <section
        id="faq"
        style={{
          background: "var(--sand-200)",
          padding: "var(--section-y) 0 48px",
        }}
      >
        <div style={{ maxWidth: 920, margin: "0 auto", padding: "0 24px" }}>
          <CpSectionHeading
            align="center"
            eyebrow="Questions"
            title="Frequently Asked Questions"
          />
          <div style={{ marginTop: 40 }}>
            <CpAccordion items={FAQS} />
          </div>
          <div
            style={{
              marginTop: 24,
              background: "var(--white)",
              border: "1px solid var(--border-default)",
              borderRadius: 16,
              padding: "24px 28px",
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: "16px 24px",
            }}
          >
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                background: "var(--ink-500)",
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
              }}
            >
              <CpIcon color="var(--gold-400)" name="mail" size={22} />
            </div>
            <div style={{ flex: 1, minWidth: 240 }}>
              <p style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>
                Trouble registering or have a question?
              </p>
              <p
                style={{
                  margin: "4px 0 0",
                  fontSize: 15,
                  color: "var(--text-muted)",
                }}
              >
                Contact CertainPath&apos;s Leslie Ramirez.
              </p>
            </div>
            <CpButton
              href="mailto:lramirez@mycertainpath.com"
              variant="secondary"
            >
              lramirez@mycertainpath.com
            </CpButton>
          </div>
        </div>
      </section>

      <section
        style={{
          background: "var(--sand-200)",
          padding: "48px 0 var(--section-y)",
        }}
      >
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px" }}>
          <div
            style={{
              background: "var(--gradient-brand)",
              borderRadius: 24,
              padding: "clamp(56px, 7vw, 88px) clamp(24px, 5vw, 56px)",
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 18,
              boxShadow: "var(--shadow-lg)",
            }}
          >
            <h2
              style={{
                margin: 0,
                color: "var(--cream-100)",
                fontSize: "var(--text-display-2)",
                lineHeight: 1.08,
                letterSpacing: "-.025em",
                maxWidth: "20ch",
                textWrap: "balance",
              }}
            >
              Bring Your Questions to the Next Call
            </h2>
            <p
              style={{
                margin: 0,
                color: "var(--cream-100)",
                fontSize: 18,
                lineHeight: 1.55,
                maxWidth: "52ch",
              }}
            >
              Coach Connect is open to CertainPath members. Join and start
              learning alongside owners working the same problems.
            </p>
          </div>
        </div>
      </section>
    </CpLandingShell>
  );
};

export default CoachConnectPage;
